import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ShieldPlus, Users as UsersIcon, Globe2, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { AccessEditor } from "@/components/AccessEditor";
import { SCOPE_LABELS, SCOPE_BASE } from "@/lib/helpers";

const SCOPES = [
  { key: "all", icon: Globe2, hint: "Tüm şubelerin satış ve raporlarını görür" },
  { key: "branch", icon: Building2, hint: "Sadece kullanıcının bağlı olduğu şubeyi görür" },
  { key: "self", icon: User, hint: "Sadece kendi girdiği satışları görür, satış girebilir" },
];
const empty = { name: "", scope: "branch", permissions: {}, include_in_targets: false };
const inTargets = (r) => r.include_in_targets ?? r.scope === "self";

function RoleCard({ r, onEdit, onDelete }) {
  const S = SCOPES.find((s) => s.key === r.scope);
  const custom = Object.keys(r.permissions || {}).length;
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass p-4 flex flex-col gap-3 animate-fade-up" data-testid={`role-card-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display font-bold text-lg" data-testid={`role-name-${r.id}`}>{r.name}</div>
          <div className="text-xs text-blue-300 flex items-center gap-1 mt-0.5"><S.icon size={12} /> {SCOPE_LABELS[r.scope]}</div>
        </div>
        <div className="flex">
          <Button size="sm" variant="ghost" onClick={() => onEdit(r)} data-testid={`role-edit-${r.id}`}><Pencil size={14} /></Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(r)} data-testid={`role-delete-${r.id}`}><Trash2 size={14} className="text-red-400" /></Button>
        </div>
      </div>
      <div className="flex gap-3 text-xs text-slate-400">
        <span className="flex items-center gap-1"><UsersIcon size={12} /> {r.user_count} kullanıcı</span>
        <span>· {custom} özel yetki ayarı</span>
        {inTargets(r) && <span className="text-emerald-300" data-testid={`role-in-targets-${r.id}`}>· Hedefe dahil</span>}
      </div>
    </Card>
  );
}

function ScopePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {SCOPES.map((s) => (
        <button key={s.key} type="button" onClick={() => onChange(s.key)} data-testid={`role-scope-${s.key}`}
                className={`rounded-xl border p-2.5 text-left transition-colors ${value === s.key ? "border-blue-500 bg-blue-600/15" : "border-slate-700 hover:border-slate-500"}`}>
          <s.icon size={16} className={value === s.key ? "text-blue-300" : "text-slate-400"} />
          <div className="text-xs font-semibold mt-1">{SCOPE_LABELS[s.key]}</div>
          <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{s.hint}</div>
        </button>
      ))}
    </div>
  );
}

export default function Roles() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => http.get("/roles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Rol adı zorunlu"); return; }
    const body = { name: form.name, scope: form.scope, permissions: form.permissions, include_in_targets: inTargets(form) };
    try {
      if (form.id) await http.put(`/roles/${form.id}`, body); else await http.post("/roles", body);
      toast.success("Rol kaydedildi"); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`"${r.name}" rolü silinsin mi?`)) return;
    try { await http.delete(`/roles/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><ShieldPlus className="text-blue-400" /> Roller</h1>
          <p className="text-slate-400 text-sm mt-1">Yeni roller tanımlayın, görünürlük kapsamını ve işlem yetkilerini belirleyin</p>
        </div>
        <Button onClick={() => setForm(empty)} className="bg-blue-600 hover:bg-blue-500" data-testid="role-new"><Plus size={16} className="mr-1" /> Yeni Rol</Button>
      </header>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="roles-list">
        {items.map((r) => <RoleCard key={r.id} r={r} onEdit={(x) => setForm({ ...x })} onDelete={remove} />)}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-12 text-sm">Henüz özel rol yok. Patron, Koordinatör, Şube Müdürü ve Personel sabit rollerdir.</div>}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        {form && (
          <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="role-dialog">
            <DialogHeader><DialogTitle>{form.id ? "Rolü Düzenle" : "Yeni Rol"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Rol Adı</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn. Bölge Sorumlusu"
                       className="bg-slate-950/50 border-slate-700" data-testid="role-form-name" />
              </div>
              <div><Label className="mb-2 block">Görünürlük Kapsamı</Label><ScopePicker value={form.scope} onChange={(v) => setForm({ ...form, scope: v })} /></div>
              <div className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2.5">
                <div><div className="text-sm font-medium">Personel hedefine dahil</div>
                  <div className="text-[10px] text-slate-500">Açıksa bu roldeki kullanıcılar şube hedefinden personel gibi eşit pay alır</div></div>
                <Switch checked={inTargets(form)} onCheckedChange={(v) => setForm({ ...form, include_in_targets: v })} data-testid="role-form-targets" />
              </div>
              <AccessEditor custom role={SCOPE_BASE[form.scope]} perms={form.permissions} title="Rol Yetkileri" resetLabel="Varsayılana dön"
                            onChange={(p) => setForm((f) => ({ ...f, permissions: p }))} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)} className="border-slate-700">İptal</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="role-form-save">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
